import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { SOCIALS, SocialIcon, socialLinkProps, type Social } from "@/components/SocialLinks";

// Rendered to static HTML at build time (scripts/prerender-hero.mjs), so keep it
// free of browser-only APIs and entrance animations that start hidden.
const profilePhoto = "/profile.webp";

// Static hero that the build puts just before #root. React leaves it in place on the first
// render instead of painting a copy, so the hero text can't become a second, later LCP.
let prerendered: HTMLElement | null =
  typeof document !== "undefined" ? document.getElementById("hero-shell") : null;

// Online between 9:00 AM and 12:00 AM IST
const isOnlineNow = () => {
  const hours = Number(new Date().toLocaleString("en-US", { hour: "numeric", hour12: false, timeZone: "Asia/Kolkata" }));
  return hours >= 9 && hours <= 23;
};

export function Hero() {
  const online = isOnlineNow();
  // Only on the very first render, and only if the build-time status dot is still correct
  const [shell] = useState(() =>
    prerendered?.querySelector("section")?.dataset.online === String(online) ? prerendered : null
  );

  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    if (!shell) prerendered?.remove();
    prerendered = null;
    // The static avatar has no React handler, so wire it up by hand
    const avatar = shell?.querySelector<HTMLButtonElement>("[data-avatar]");
    const open = () => setProfileOpen(true);
    avatar?.addEventListener("click", open);
    return () => {
      avatar?.removeEventListener("click", open);
      shell?.remove(); // leaving home: later visits render normally
    };
  }, [shell]);

  const modal = <ProfileCard open={profileOpen} onClose={() => setProfileOpen(false)} />;
  if (shell) return modal;

  return (
    <>
    <section id="hero" data-online={String(online)} className="px-4 sm:px-6 pt-16 md:pt-24 pb-6 md:pb-8 bg-background">
      <div className="site-frame">
        <div className="flex items-center gap-4 animate-hero-in motion-reduce:animate-none">
          <div className="relative shrink-0">
            {/* scale crops the photo's white corner edge inside the circle */}
            <button
              type="button"
              data-avatar
              onClick={() => setProfileOpen(true)}
              aria-label="Open profile card"
              className="block w-12 h-12 rounded-full overflow-hidden cursor-pointer transition-transform duration-200 hover:scale-105 active:scale-95"
            >
              <img
                src={profilePhoto}
                alt="Chirag Gadhvi"
                width={48}
                height={48}
                {...{ fetchpriority: "high" }}
                className="w-full h-full object-cover scale-[1.04]"
              />
            </button>
            <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-background">
              <span className="relative flex h-2 w-2">
                {online && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />}
                <span className={`relative inline-flex h-2 w-2 rounded-full ${online ? "bg-green-500" : "bg-red-500"}`} />
              </span>
            </span>
          </div>

          <div>
            <h1 className="text-lg md:text-xl font-semibold tracking-tight text-foreground">Chirag Gadhvi</h1>
            <p className="text-sm text-muted-foreground">Software and AI Engineer</p>
          </div>
        </div>

        <p className="mt-6 text-sm md:text-[15px] leading-relaxed text-muted-foreground animate-hero-in [animation-delay:150ms] motion-reduce:animate-none">
          I build websites, mobile apps and AI tools that make everyday work easier. At ShipConsole, I manage
          the cloud that keeps our product fast, reliable and running smoothly. I care about how things look and feel just as much as
          how well they work.
        </p>
      </div>
    </section>
    {modal}
    </>
  );
}

function ProfileCard({ open, onClose }: { open: boolean; onClose: () => void }) {
  useEffect(() => {
    if (!open) return;
    const lenis = (window as { lenis?: { stop: () => void; start: () => void } }).lenis;
    lenis?.stop();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      lenis?.start();
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (typeof document === "undefined") return null;
  // Portal: the page-transition wrapper's transform/filter would otherwise trap `fixed`
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-background/60 p-4 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label="Chirag Gadhvi"
        >
          <motion.div
            className="relative flex w-full max-w-[20rem] flex-col items-center rounded-2xl border border-border bg-card px-6 pb-6 pt-6 shadow-2xl"
            initial={{ opacity: 0, scale: 0.92, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ type: "spring", stiffness: 380, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute right-3 top-3 rounded-full p-1.5 text-muted-foreground transition-colors hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
            <Orbit />
            <p className="mt-3 text-base font-semibold tracking-tight text-foreground">Chirag Gadhvi</p>
            <p className="text-xs text-muted-foreground">Software and AI Engineer</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

// Socials circle the photo. The ring spins slowly; each icon counter-spins to stay upright,
// and hovering the ring pauses it so the icons are easy to hit.
const ORBIT_RADIUS = 92;
const spin = "animate-[spin_40s_linear_infinite] group-hover/orbit:[animation-play-state:paused] motion-reduce:animate-none";

// Also used in the phone chat drawer (with the extra coding profiles)
export function Orbit({ socials = SOCIALS }: { socials?: Social[] }) {
  return (
    <div className="group/orbit relative h-56 w-56">
      <div className="absolute inset-0 m-auto h-28 w-28 overflow-hidden rounded-full">
        <img src={profilePhoto} alt="Chirag Gadhvi" width={112} height={112} className="h-full w-full scale-[1.04] object-cover" />
      </div>
      <div className="absolute inset-0 rounded-full border border-dashed border-border" style={{ margin: 112 - ORBIT_RADIUS }} />
      <div className={`absolute inset-0 ${spin}`}>
        {socials.map((social, i) => {
          const angle = (360 / socials.length) * i - 90;
          return (
            <div
              key={social.label}
              className="absolute left-1/2 top-1/2 -ml-[18px] -mt-[18px]"
              style={{ transform: `rotate(${angle}deg) translate(${ORBIT_RADIUS}px) rotate(${-angle}deg)` }}
            >
              <div className={`${spin} [animation-direction:reverse]`}>
                <motion.a
                  {...socialLinkProps(social)}
                  initial={{ opacity: 0, scale: 0.4 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.08 + i * 0.04, type: "spring", stiffness: 420, damping: 22 }}
                  whileHover={{ scale: 1.15 }}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background shadow-sm"
                >
                  <SocialIcon social={social} className="h-[18px] w-[18px]" />
                </motion.a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
